import { ArcButton, ArcStack } from '@src/core/ui';
import React from 'react';
import type { BarChartType } from '../BarChart/types';
import { ChartTypeSwitcherProps } from './types';

function formatDateDDMMYYYY(d: Date): string {
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

/** Rolling 12-month window: end = today + (periodOffset * 12 months), start = end - 12 months. */
function getMonthlyRollingWindow(periodOffset: number): { start: Date; end: Date } {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth() + periodOffset * 12, now.getDate());
  const start = new Date(end.getFullYear(), end.getMonth() - 12, end.getDate());
  return { start, end };
}

export function getChartCaptionInterval(type: BarChartType, periodOffset = 0): string {
  const now = new Date();
  let start: Date;
  let end: Date;
  if (type === 'monthly') {
    const window = getMonthlyRollingWindow(periodOffset);
    start = window.start;
    end = window.end;
  } else if (type === 'daily') {
    // Daily: one week (7 days), periodOffset 0 = this week, -1 = previous week
    start = getChartIntervalStartDate(type, periodOffset);
    end = getChartIntervalEndDate(type, periodOffset);
  } else {
    // Weekly: periodOffset is in 4-week blocks (0 = current 4 weeks, -4 = previous 4, etc.)
    start = getChartIntervalStartDate(type, periodOffset - 3);
    end = getChartIntervalEndDate(type, periodOffset);
  }
  return `${formatDateDDMMYYYY(start)} - ${formatDateDDMMYYYY(end)}`;
}

/** Returns the start date of the chart interval for the given type and period offset. */
export function getChartIntervalStartDate(type: BarChartType, periodOffset = 0): Date {
  const now = new Date();
  if (type === 'monthly') {
    return getMonthlyRollingWindow(periodOffset).start;
  }
  // Weekly: periodOffset is in 4-week blocks (0, -4, -8, ...)
  const dayOfWeek = now.getDay();
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const start = new Date(now);
  start.setDate(now.getDate() - daysToMonday + periodOffset * 7);
  return start;
}

/** Returns the end date of the chart interval for the given type and period offset. */
export function getChartIntervalEndDate(type: BarChartType, periodOffset = 0): Date {
  const now = new Date();
  if (type === 'monthly') {
    return getMonthlyRollingWindow(periodOffset).end;
  }
  // Weekly: periodOffset is in 4-week blocks
  const dayOfWeek = now.getDay();
  const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const start = new Date(now);
  start.setDate(now.getDate() - daysToMonday + periodOffset * 7);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return end;
}

/** Returns start and end dates as YYYY-MM-DD for API (daily chart interval). */
export function getChartIntervalDates(
  type: BarChartType,
  periodOffset = 0
): { startDate: string; endDate: string } {
  const start = getChartIntervalStartDate(type, periodOffset);
  const end = getChartIntervalEndDate(type, periodOffset);
  const toYYYYMMDD = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { startDate: toYYYYMMDD(start), endDate: toYYYYMMDD(end) };
}

/** ISO week number (1–53). Week 1 is the week with the first Thursday of the year. */
function getISOWeekNumber(d: Date): number {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const thursday = new Date(date);
  thursday.setDate(date.getDate() - ((date.getDay() + 6) % 7) + 3);
  const firstThursday = new Date(thursday.getFullYear(), 0, 4);
  return 1 + Math.round(((thursday.getTime() - firstThursday.getTime()) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7);
}

/** Returns the API period string for weekly (e.g. "2026-W08") for the given period offset. */
export function getWeeklyPeriodString(periodOffset: number): string {
  const start = getChartIntervalStartDate('weekly', periodOffset);
  const thursday = new Date(start);
  thursday.setDate(start.getDate() + 3);
  const isoYear = thursday.getFullYear();
  const week = getISOWeekNumber(start);
  return `${isoYear}-W${String(week).padStart(2, '0')}`;
}

/** For WEEKLY: week number and year. For MONTHLY: only year (current year + periodOffset). For DAILY: startDate/endDate. */
export function getChartIntervalApiParams(
  type: BarChartType,
  periodOffset = 0
): { startDate?: string; endDate?: string; week?: number; year?: number } {
  if (type === 'monthly') {
    const end = getChartIntervalEndDate(type, periodOffset);
    return { year: end.getFullYear() };
  }
  if (type === 'weekly') {
    const start = getChartIntervalStartDate(type, periodOffset);
    const week = getISOWeekNumber(start);
    return { week, year: start.getFullYear() };
  }
  const { startDate, endDate } = getChartIntervalDates(type, periodOffset);
  return { startDate, endDate };
}

export const CHART_TYPE_OPTIONS: { value: BarChartType; label: string; caption: string }[] = [
  { value: 'daily', label: 'Daily', caption: 'This week' },
  { value: 'weekly', label: 'Weekly', caption: 'This week' },
  { value: 'monthly', label: 'Monthly', caption: 'This month' },
];

const SWITCHER_SX = {
  border: '1px solid',
  padding: '3px',
  borderRadius: '19px',
  backgroundColor: 'text.secondary',
} as const;

const BUTTON_SX = (selected: boolean) => ({
  minWidth: 60,
  borderRadius: '16px',
  fontSize: '12px',
  textTransform: 'none' as const,
  color: 'black !important',
  fontWeight: selected ? 700 : 400,
  bgcolor: selected ? 'white' : 'text.secondary',
  '&:hover': {
    bgcolor: selected ? 'grey.200' : 'text.secondary',
  },
});


export const ChartTypeSwitcher: React.FC<ChartTypeSwitcherProps> = ({ value, onChange }) => (
  <ArcStack direction="row" gap={0} sx={SWITCHER_SX}>
    {CHART_TYPE_OPTIONS.map(({ value: optionValue, label }) => (
      <ArcButton
        key={optionValue}
        onClick={() => onChange(optionValue)}
        sx={BUTTON_SX(value === optionValue)}
      >
        {label}
      </ArcButton>
    ))}
  </ArcStack>
);
